import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { LOGO_URL } from "@/i18n";

const NAV = [
  { id: "services", key: "services" },
  { id: "pricing", key: "pricing" },
  { id: "clients", key: "work" },
  { id: "why", key: "why" },
  { id: "tech", key: "tech" },
  { id: "owner", key: "owner" },
  { id: "contact", key: "contact" },
];

const Header = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-1 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center gap-3"
          data-testid="header-logo-button"
        >
          <img
            src={LOGO_URL}
            alt="Dhon Studio"
            className="h-10 w-10 md:h-14 md:w-14 object-contain"
          />
          {/* <span className="hidden sm:block font-display text-lg tracking-tight">
            DHON<span className="text-[#ccff00]"> </span>STUDIO
          </span> */}
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="nav-link text-sm font-medium text-white/80 hover:text-white"
              data-testid={`nav-${item.id}`}
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="pill sm:pill-ghost text-sm !py-2 !px-4"
            data-testid="language-toggle"
            aria-label="Toggle language"
          >
            <span className={lang === "id" ? "text-[#ccff00]" : "text-white/50"}>ID</span>
            <span className="text-white/30">/</span>
            <span className={lang === "en" ? "text-[#ccff00]" : "text-white/50"}>EN</span>
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="md:hidden sm:pill sm:pill-ghost !py-2 !px-3"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            <span className="block w-5 space-y-1">
              <span className={`block h-[2px] bg-white transition ${open ? "rotate-45 translate-y-[6px]" : ""}`} />
              <span className={`block h-[2px] bg-white transition ${open ? "opacity-0" : ""}`} />
              <span className={`block h-[2px] bg-white transition ${open ? "-rotate-45 -translate-y-[6px]" : ""}`} />
            </span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-black/95 border-b border-white/10 overflow-hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="px-6 py-6 space-y-4">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="block font-display text-2xl text-left w-full hover:text-[#ccff00] transition"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {t.nav[item.key]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
